import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Save,
    Building2,
    Hash,
    Tag,
    Shield,
    Users,
    FileText,
    Plus,
    X,
    Loader2,
    Check,
    ScrollText,
    MapPin,
    DollarSign,
    Award,
    Lock
} from 'lucide-react';
import { getContractor, saveContractor, SEED_CONTRACTORS } from '../services/contractors';
import { Contractor } from '../types';

export default function ContractorProfile() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [contractor, setContractor] = useState<Contractor | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const [activeTab, setActiveTab] = useState('general');

    // Form States
    const [newNaics, setNewNaics] = useState('');
    const [newKeyword, setNewKeyword] = useState('');
    const [newClearance, setNewClearance] = useState('');
    const [newCert, setNewCert] = useState('');
    const [newLocation, setNewLocation] = useState('');

    useEffect(() => {
        loadContractor();
    }, [id]);

    const loadContractor = async () => {
        if (!id) return;
        setLoading(true);
        try {
            let data = await getContractor(id);
            if (!data) {
                data = SEED_CONTRACTORS.find(c => c.id === id) || null;
            }
            setContractor(data);
        } catch (err) {
            console.error('Error loading contractor:', err);
            const seedData = SEED_CONTRACTORS.find(c => c.id === id);
            setContractor(seedData || null);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!contractor) return;
        setSaving(true);
        try {
            await saveContractor(contractor);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (err) {
            console.error('Error saving contractor:', err);
            alert('Failed to save changes. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const addNaicsCode = () => {
        if (!contractor || !newNaics.trim()) return;
        if (contractor.naicsCodes.includes(newNaics.trim())) return;
        setContractor({
            ...contractor,
            naicsCodes: [...contractor.naicsCodes, newNaics.trim()]
        });
        setNewNaics('');
    };

    const removeNaicsCode = (code: string) => {
        if (!contractor) return;
        setContractor({
            ...contractor,
            naicsCodes: contractor.naicsCodes.filter(c => c !== code)
        });
    };

    const addKeyword = () => {
        if (!contractor || !newKeyword.trim()) return;
        if (contractor.keywords.includes(newKeyword.trim().toLowerCase())) return;
        setContractor({
            ...contractor,
            keywords: [...contractor.keywords, newKeyword.trim().toLowerCase()]
        });
        setNewKeyword('');
    };

    const removeKeyword = (keyword: string) => {
        if (!contractor) return;
        setContractor({
            ...contractor,
            keywords: contractor.keywords.filter(k => k !== keyword)
        });
    };

    // New Helpers
    const updatePersonnel = (field: string, value: any) => {
        if (!contractor) return;
        setContractor({
            ...contractor,
            personnel: {
                count: contractor.personnel?.count || 0,
                clearances: contractor.personnel?.clearances || [],
                certifications: contractor.personnel?.certifications || [],
                ...contractor.personnel,
                [field]: value
            }
        });
    };

    const addListItem = (section: 'personnel' | 'preferences', field: string, value: string, setter: (v: string) => void) => {
        if (!contractor || !value.trim()) return;

        const currentList = section === 'personnel'
            ? (contractor.personnel?.[field as keyof typeof contractor.personnel] as string[] || [])
            : (contractor.preferences?.[field as keyof typeof contractor.preferences] as string[] || []);

        if (currentList.includes(value.trim())) return;

        if (section === 'personnel') {
            setContractor({
                ...contractor,
                personnel: {
                    count: contractor.personnel?.count || 0,
                    clearances: contractor.personnel?.clearances || [],
                    certifications: contractor.personnel?.certifications || [],
                    ...contractor.personnel,
                    [field]: [...currentList, value.trim()]
                }
            });
        } else {
            setContractor({
                ...contractor,
                preferences: {
                    ...contractor.preferences,
                    [field]: [...currentList, value.trim()]
                }
            });
        }
        setter('');
    };

    const removeListItem = (section: 'personnel' | 'preferences', field: string, value: string) => {
        if (!contractor) return;
        const currentList = section === 'personnel'
            ? (contractor.personnel?.[field as keyof typeof contractor.personnel] as string[] || [])
            : (contractor.preferences?.[field as keyof typeof contractor.preferences] as string[] || []);

        if (section === 'personnel') {
            setContractor({
                ...contractor,
                personnel: {
                    ...contractor.personnel!,
                    [field]: currentList.filter(i => i !== value)
                }
            });
        } else {
            setContractor({
                ...contractor,
                preferences: {
                    ...contractor.preferences!,
                    [field]: currentList.filter(i => i !== value)
                }
            });
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        );
    }

    if (!contractor) {
        return (
            <div className="text-center py-20">
                <p className="text-secondary">Contractor not found</p>
                <button onClick={() => navigate('/contractors')} className="btn btn-primary mt-4">
                    Back to Contractors
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6 fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/contractors')}
                        className="p-2 hover:bg-tertiary rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 text-secondary" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-primary">{contractor.name}</h1>
                        <p className="text-sm text-secondary">Contractor Profile</p>
                    </div>
                </div>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className={`btn ${saved ? 'bg-green-500 hover:bg-green-600' : 'btn-primary'}`}
                >
                    {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : saved ? (
                        <Check className="w-4 h-4" />
                    ) : (
                        <Save className="w-4 h-4" />
                    )}
                    {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
                </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-white/10 flex gap-6">
                <button
                    onClick={() => setActiveTab('general')}
                    className={`pb-4 text-sm font-medium transition-colors border-b-2 ${activeTab === 'general' ? 'border-blue-500 text-blue-400' : 'border-transparent text-secondary hover:text-primary'
                        }`}
                >
                    General
                </button>
                <button
                    onClick={() => setActiveTab('capabilities')}
                    className={`pb-4 text-sm font-medium transition-colors border-b-2 ${activeTab === 'capabilities' ? 'border-blue-500 text-blue-400' : 'border-transparent text-secondary hover:text-primary'
                        }`}
                >
                    Capabilities
                </button>
                <button
                    onClick={() => setActiveTab('resources')}
                    className={`pb-4 text-sm font-medium transition-colors border-b-2 ${activeTab === 'resources' ? 'border-blue-500 text-blue-400' : 'border-transparent text-secondary hover:text-primary'
                        }`}
                >
                    Resources
                </button>
                <button
                    onClick={() => setActiveTab('preferences')}
                    className={`pb-4 text-sm font-medium transition-colors border-b-2 ${activeTab === 'preferences' ? 'border-blue-500 text-blue-400' : 'border-transparent text-secondary hover:text-primary'
                        }`}
                >
                    Preferences
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'general' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-in fade-in zoom-in-95 duration-200">
                    {/* NAICS Codes */}
                    <div className="card p-5">
                        <h2 className="text-xs font-semibold text-tertiary uppercase tracking-wider flex items-center gap-2 mb-4">
                            <Hash className="w-4 h-4" />
                            NAICS Codes ({contractor.naicsCodes.length})
                        </h2>
                        <div className="flex gap-2 mb-4">
                            <input
                                type="text"
                                value={newNaics}
                                onChange={(e) => setNewNaics(e.target.value)}
                                placeholder="e.g. 541512"
                                className="input-field flex-1"
                                onKeyDown={(e) => e.key === 'Enter' && addNaicsCode()}
                            />
                            <button onClick={addNaicsCode} className="btn btn-secondary">
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {contractor.naicsCodes.map((code) => (
                                <span key={code} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg text-sm font-mono">
                                    {code}
                                    <button onClick={() => removeNaicsCode(code)} className="hover:text-red-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Keywords */}
                    <div className="card p-5">
                        <h2 className="text-xs font-semibold text-tertiary uppercase tracking-wider flex items-center gap-2 mb-4">
                            <Tag className="w-4 h-4" />
                            Keywords ({contractor.keywords.length})
                        </h2>
                        <div className="flex gap-2 mb-4">
                            <input
                                type="text"
                                value={newKeyword}
                                onChange={(e) => setNewKeyword(e.target.value)}
                                placeholder="e.g. data labeling"
                                className="input-field flex-1"
                                onKeyDown={(e) => e.key === 'Enter' && addKeyword()}
                            />
                            <button onClick={addKeyword} className="btn btn-secondary">
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {contractor.keywords.map((keyword) => (
                                <span key={keyword} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 text-green-400 rounded-lg text-sm">
                                    {keyword}
                                    <button onClick={() => removeKeyword(keyword)} className="hover:text-red-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Contacts */}
                    <div className="card p-5">
                        <h2 className="text-xs font-semibold text-tertiary uppercase tracking-wider flex items-center gap-2 mb-4">
                            <Users className="w-4 h-4" />
                            Contacts
                        </h2>
                        <div className="space-y-3">
                            {contractor.contacts.map((contact, idx) => (
                                <div key={idx} className="p-3 bg-tertiary rounded-lg">
                                    <p className="font-medium text-primary">{contact.name}</p>
                                    <p className="text-sm text-secondary">{contact.role}</p>
                                    {contact.email && <a href={`mailto:${contact.email}`} className="text-sm text-blue-400 hover:underline">{contact.email}</a>}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="card p-5">
                        <h2 className="text-xs font-semibold text-tertiary uppercase tracking-wider flex items-center gap-2 mb-4">
                            <FileText className="w-4 h-4" />
                            Notes
                        </h2>
                        <textarea
                            value={contractor.notes || ''}
                            onChange={(e) => setContractor({ ...contractor, notes: e.target.value })}
                            className="input-field min-h-[120px] resize-none"
                            placeholder="Add notes about this contractor..."
                        />
                    </div>
                </div>
            )}

            {activeTab === 'capabilities' && (
                <div className="card p-6 animate-in fade-in zoom-in-95 duration-200">
                    <h2 className="text-lg font-semibold text-primary mb-2 flex items-center gap-2">
                        <ScrollText className="w-5 h-5 text-purple-400" />
                        Capability Statement
                    </h2>
                    <p className="text-secondary text-sm mb-4">
                        Paste your full capability statement here. The AI will use this to better understand your company's core competencies, past performance, and differentiators.
                    </p>
                    <textarea
                        value={contractor.capabilityStatement || ''}
                        onChange={(e) => setContractor({ ...contractor, capabilityStatement: e.target.value })}
                        className="input-field min-h-[400px] font-mono text-sm leading-relaxed"
                        placeholder="# Corporate Overview&#10;We are a..."
                    />
                </div>
            )}

            {activeTab === 'resources' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-in fade-in zoom-in-95 duration-200">
                    {/* Personnel Count */}
                    <div className="card p-5">
                        <h2 className="text-xs font-semibold text-tertiary uppercase tracking-wider flex items-center gap-2 mb-4">
                            <Users className="w-4 h-4" />
                            Staffing Capacity
                        </h2>
                        <div className="flex items-center gap-4">
                            <div className="flex-1">
                                <label className="text-sm text-secondary mb-1 block">Total Employees</label>
                                <input
                                    type="number"
                                    value={contractor.personnel?.count || 0}
                                    onChange={(e) => updatePersonnel('count', parseInt(e.target.value) || 0)}
                                    className="input-field"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Clearances */}
                    <div className="card p-5">
                        <h2 className="text-xs font-semibold text-tertiary uppercase tracking-wider flex items-center gap-2 mb-4">
                            <Lock className="w-4 h-4" />
                            Security Clearances
                        </h2>
                        <div className="flex gap-2 mb-4">
                            <select
                                value={newClearance}
                                onChange={(e) => setNewClearance(e.target.value)}
                                className="input-field flex-1"
                            >
                                <option value="">Select Clearance...</option>
                                <option value="Public Trust">Public Trust</option>
                                <option value="Secret">Secret</option>
                                <option value="Top Secret">Top Secret</option>
                                <option value="TS/SCI">TS/SCI</option>
                                <option value="DOE Q">DOE Q</option>
                                <option value="DOE L">DOE L</option>
                            </select>
                            <button
                                onClick={() => addListItem('personnel', 'clearances', newClearance, setNewClearance)}
                                className="btn btn-secondary"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {(contractor.personnel?.clearances || []).map((c) => (
                                <span key={c} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 text-amber-400 rounded-lg text-sm">
                                    {c}
                                    <button onClick={() => removeListItem('personnel', 'clearances', c)} className="hover:text-red-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Certifications */}
                    <div className="card p-5 lg:col-span-2">
                        <h2 className="text-xs font-semibold text-tertiary uppercase tracking-wider flex items-center gap-2 mb-4">
                            <Award className="w-4 h-4" />
                            Certifications
                        </h2>
                        <div className="flex gap-2 mb-4">
                            <input
                                type="text"
                                value={newCert}
                                onChange={(e) => setNewCert(e.target.value)}
                                placeholder="e.g. ISO 9001, CMMI Level 3"
                                className="input-field flex-1"
                                onKeyDown={(e) => e.key === 'Enter' && addListItem('personnel', 'certifications', newCert, setNewCert)}
                            />
                            <button onClick={() => addListItem('personnel', 'certifications', newCert, setNewCert)} className="btn btn-secondary">
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {(contractor.personnel?.certifications || []).map((c) => (
                                <span key={c} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg text-sm">
                                    {c}
                                    <button onClick={() => removeListItem('personnel', 'certifications', c)} className="hover:text-red-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'preferences' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-in fade-in zoom-in-95 duration-200">
                    {/* Contract Value */}
                    <div className="card p-5">
                        <h2 className="text-xs font-semibold text-tertiary uppercase tracking-wider flex items-center gap-2 mb-4">
                            <DollarSign className="w-4 h-4" />
                            Contract Value Preferences
                        </h2>
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm text-secondary mb-1 block">Minimum Value ($)</label>
                                <input
                                    type="number"
                                    value={contractor.preferences?.minContractValue || ''}
                                    onChange={(e) => setContractor({
                                        ...contractor,
                                        preferences: { ...contractor.preferences, minContractValue: parseInt(e.target.value) }
                                    })}
                                    className="input-field"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-secondary mb-1 block">Maximum Value ($)</label>
                                <input
                                    type="number"
                                    value={contractor.preferences?.maxContractValue || ''}
                                    onChange={(e) => setContractor({
                                        ...contractor,
                                        preferences: { ...contractor.preferences, maxContractValue: parseInt(e.target.value) }
                                    })}
                                    className="input-field"
                                    placeholder="No limit"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Locations */}
                    <div className="card p-5">
                        <h2 className="text-xs font-semibold text-tertiary uppercase tracking-wider flex items-center gap-2 mb-4">
                            <MapPin className="w-4 h-4" />
                            Preferred Locations
                        </h2>
                        <div className="flex gap-2 mb-4">
                            <input
                                type="text"
                                value={newLocation}
                                onChange={(e) => setNewLocation(e.target.value)}
                                placeholder="e.g. Virginia, DC, Remote"
                                className="input-field flex-1"
                                onKeyDown={(e) => e.key === 'Enter' && addListItem('preferences', 'preferredLocations', newLocation, setNewLocation)}
                            />
                            <button onClick={() => addListItem('preferences', 'preferredLocations', newLocation, setNewLocation)} className="btn btn-secondary">
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {(contractor.preferences?.preferredLocations || []).map((l) => (
                                <span key={l} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 text-purple-400 rounded-lg text-sm">
                                    {l}
                                    <button onClick={() => removeListItem('preferences', 'preferredLocations', l)} className="hover:text-red-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Reuse existing sections for Set-Asides and preferred Agencies in Preferences tab as well? 
                        No, keep them in General or just duplicate. Existing design had them. 
                        Let's move Preferred Agencies and Set-Asides to Preferences tab logic-wise, but for now I removed them from General. 
                        I should check if I missed them. 
                        My 'General' section replacement included NAICS, Keywords, Contacts, Notes. 
                        I MISSED 'Preferred Agencies' and 'Set-Asides' which were in the original.
                        I should probably put them in 'Preferences' or 'General'. 
                        
                        Restoring them to 'Preferences' tab seems cleaner.
                    */}

                    {/* Agency Preferences */}
                    <div className="card p-5">
                        <h2 className="text-xs font-semibold text-tertiary uppercase tracking-wider flex items-center gap-2 mb-4">
                            <Building2 className="w-4 h-4" />
                            Preferred Agencies
                        </h2>
                        {/* Note: I need to add editing logic for this if I want to keep it consistent, 
                            but for now just showing the list from the original code or making it editable. 
                            The original code didn't have add/remove for these! It just displayed them. 
                            I should make them editable. 
                        */}
                        <div className="flex flex-wrap gap-2">
                            {contractor.preferredAgencies.map((agency) => (
                                <span key={agency} className="px-3 py-1.5 bg-purple-500/10 text-purple-400 rounded-lg text-sm">
                                    {agency}
                                </span>
                            ))}
                        </div>
                        <p className="text-xs text-secondary mt-2 italic">Note: Agency preferences editing coming soon.</p>
                    </div>

                    {/* Set-Aside Preferences */}
                    <div className="card p-5">
                        <h2 className="text-xs font-semibold text-tertiary uppercase tracking-wider flex items-center gap-2 mb-4">
                            <Shield className="w-4 h-4" />
                            Set-Aside Status
                        </h2>
                        <div className="flex flex-wrap gap-2">
                            {contractor.preferredSetAsides.map((setAside) => (
                                <span key={setAside} className="px-3 py-1.5 bg-amber-500/10 text-amber-400 rounded-lg text-sm">
                                    {setAside}
                                </span>
                            ))}
                        </div>
                        <p className="text-xs text-secondary mt-2 italic">Note: Set-aside preferences editing coming soon.</p>
                    </div>
                </div>
            )}
        </div>
    );
}
